import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo
} from "react";
import useLocalStorage from '@d2k/react-localstorage'
import { useQueryClient, useQuery } from '@tanstack/react-query'
import { CircularProgress, Box } from "@mui/material";
import i18n from "./i18n";

import { axiosInstance } from './api/axios-client';
import {
  getGetUserByTokenQueryOptions,
  getListUserPermissionsQueryOptions,
  getGetTenantsFromLoggedUserQueryOptions,
  getListAllOrganizationQueryOptions,
  loginWithKeycloakToken
} from "./api/bpm/endpoints";
import type { SmartPermissionResumoDto, SmartUserWithAllPropertiesDto, OrganizationDto } from "./api/bpm/model";
import { setAuthToken, setTenantHeader } from "./api/axios-client";

export type PostOauthTokenResponse = {
  accessToken?: string;
  expiresIn?: number;
  refreshExpiresIn?: number;
  refreshToken?: string;
  tokenType?: string;
};
import { env } from "./utils/env";

export interface AuthContext {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: { login: string; password: string; rememberMe?: boolean }) => Promise<void>;
  loginWithKeycloak: (keycloakToken: string) => Promise<void>;
  logout: () => Promise<void>;
  user?: SmartUserWithAllPropertiesDto;
  userTenant?: string;
  permissions: SmartPermissionResumoDto[];
  hasPermission: (module: string) => boolean;
  tenants: string[];
  organizations: OrganizationDto[];
  currentTenant: string;
  isSwitchingTenant: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
}

const DEFAULT_TENANT = env.VITE_APP_DEFAULT_TENANT || "default-domain";

const getDynamicBaseUrl = (envUrl: string | undefined, defaultBase: string) => {
  const url = envUrl || '';
  if (url && url.startsWith('http')) {
    if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) {
      return defaultBase;
    }
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.replace(/\/$/, '');
      if (pathname && pathname !== '/') {
        return `${pathname}${defaultBase.replace('/api', '')}`;
      }
    } catch (e) {
    }
  }
  return defaultBase;
};

const applyHeadersConfig = (token?: string, tenant: string = DEFAULT_TENANT) => {
  const headers: Record<string, string> = {
    tenant: tenant
  }

  let cleanToken = token;
  if (token) {
    cleanToken = token.startsWith('Bearer ') ? token.slice(7) : token;
    headers.Authorization = `Bearer ${cleanToken}`
  }

  setAuthToken(cleanToken);
  setTenantHeader(tenant);
}

applyHeadersConfig()

const AuthContext = createContext<AuthContext | null>(null);
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const getStoredToken = (): PostOauthTokenResponse => {
    try {
      const ls = localStorage.getItem("token");
      if (ls) return JSON.parse(ls);
      const ss = sessionStorage.getItem("token");
      if (ss) {
        localStorage.setItem("token", ss);
        sessionStorage.removeItem("token");
        return JSON.parse(ss);
      }
    } catch {  }
    return {} as PostOauthTokenResponse;
  };

  const [token, setTokenState] = useState<PostOauthTokenResponse>(getStoredToken);

  const setToken = useCallback((val: PostOauthTokenResponse) => {
    setTokenState(val);
    const json = JSON.stringify(val);
    localStorage.setItem("token", json);
    sessionStorage.removeItem("token");
  }, []);

  const removeToken = useCallback(() => {
    setTokenState({} as PostOauthTokenResponse);
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
  }, []);
  const [isSwitchingTenant, setIsSwitchingTenant] = useState(false);
  const [isInitializing, setIsInitializing] = useState(() => !!token?.accessToken);
  const [isAuthTransitioning, setIsAuthTransitioning] = useState(false);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  const {
    data: userResponse,
    refetch: fetchUserInfo,
    isLoading: loadingUserInfo,
    isError: isUserError,
    isSuccess: isUserSuccess,
    error: userError,
  } = useQuery({
    ...getGetUserByTokenQueryOptions(),
    enabled: false,
    retry: (failureCount, error) => {
      const status = (error as any)?.response?.status;
      if (status === 401 || status === 403) return false;
      return failureCount < 3;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });
  
  const user = userResponse ? ((userResponse as any).data ?? userResponse) as SmartUserWithAllPropertiesDto : undefined;
  const [storedTenant, setStoredTenant] = useLocalStorage<string | null>("currentTenant", null);
  const [activeTenant, setActiveTenant] = useState<string>(() => {
    try {
      const item = localStorage.getItem("currentTenant");
      if (item) {
        try {
          const parsed = JSON.parse(item);
          if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
        } catch {
          if (item.trim()) return item.trim();
        }
      }
    } catch {}
    return DEFAULT_TENANT;
  });
  const currentTenant = activeTenant;

  const [hasInitializedTenant, setHasInitializedTenant] = useState(false);

  useEffect(() => {
    if (!user) {
      setHasInitializedTenant(false);
    }
  }, [user]);

  const { data: tenantsResponse, isLoading: loadingTenants } = useQuery({
    ...getGetTenantsFromLoggedUserQueryOptions(),
    enabled: !!user,
    retry: false
  });

  const tenants = useMemo<string[]>(() => {
    if (!tenantsResponse) return [];
    return ((tenantsResponse as any).data ?? tenantsResponse) as string[];
  }, [tenantsResponse]);

  const { data: organizationsResponse, isLoading: loadingOrganizations } = useQuery({
    ...getListAllOrganizationQueryOptions("default-domain"),
    enabled: !!user,
    retry: false
  });

  const organizations = useMemo<OrganizationDto[]>(() => {
    if (!organizationsResponse) return [];
    return ((organizationsResponse as any).data ?? organizationsResponse) as OrganizationDto[];
  }, [organizationsResponse]);

  const orgKeys = useMemo<string[]>(() => {
    const keysFromOrgs = (organizations || []).map(o => o.key || o.id || "").filter(Boolean);
    if (keysFromOrgs.length > 0) return keysFromOrgs;
    return (tenants || []).filter(Boolean);
  }, [organizations, tenants]);

  useEffect(() => {
    if (!user) return;
    if (loadingTenants || loadingOrganizations) return;

    const isCurrentValid = orgKeys.includes(activeTenant);
    if (hasInitializedTenant && isCurrentValid) return;

    let targetTenant: string;

    const rawLs = (() => {
      try {
        const item = localStorage.getItem("currentTenant");
        if (!item) return null;
        try {
          const parsed = JSON.parse(item);
          if (typeof parsed === "string" && parsed.trim()) return parsed.trim();
        } catch {
          if (item.trim()) return item.trim();
        }
      } catch {}
      return null;
    })();

    const cached = rawLs || storedTenant;
    const isValidCached = Boolean(cached && orgKeys.includes(cached));

    if (isValidCached && cached) {
      targetTenant = cached;
    } else if (orgKeys.length > 0) {
      targetTenant = orgKeys[0];
    } else if (user?.tenant && user.tenant.trim()) {
      targetTenant = user.tenant.trim();
    } else {
      targetTenant = DEFAULT_TENANT;
    }

    if (targetTenant !== activeTenant || !hasInitializedTenant) {
      setActiveTenant(targetTenant);
      setStoredTenant(targetTenant);
      applyHeadersConfig(token?.accessToken, targetTenant);
      setHasInitializedTenant(true);
    }
  }, [user, loadingTenants, loadingOrganizations, orgKeys, storedTenant, activeTenant, setStoredTenant, token, hasInitializedTenant]);

  useEffect(() => {
    if (token?.accessToken) {
      applyHeadersConfig(token.accessToken, currentTenant);
    }
  }, [currentTenant, token]);

  useEffect(() => {
    if (isUserError) {
      const status = (userError as any)?.response?.status;
      const isAuthError = status === 401 || status === 403;
      if (isAuthError) {
        removeToken();
        applyHeadersConfig();
      }
      setIsInitializing(false);
      setIsAuthTransitioning(false);
    }
  }, [isUserError, userError, removeToken]);

  useEffect(() => {
    if (isUserSuccess) {
      setIsInitializing(false);
    }
  }, [isUserSuccess]);

  const { data: permissionsResponse, isLoading: loadingPermissions } = useQuery({
    ...getListUserPermissionsQueryOptions(currentTenant, user?.id || ""),
    enabled: !!user?.id && !user?.isAdmin,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const isAuthenticated = !!user;

  const permissions = useMemo<SmartPermissionResumoDto[]>(() => {
    if (!permissionsResponse) return [];
    return ((permissionsResponse as any).data ?? permissionsResponse) as SmartPermissionResumoDto[];
  }, [permissionsResponse]);

  const hasPermission = useCallback((module: string): boolean => {
    if (user?.isAdmin) return true;
    return permissions.some(p => {
      if (!p.permission) return false;
      const moduleBase = module.split(".")[0];
      return p.permission === module || p.permission === moduleBase || module.startsWith(p.permission + ".");
    });
  }, [permissions, user?.isAdmin]);

  const logout = useCallback(async () => {
    setIsAuthTransitioning(true);
    removeToken();
    applyHeadersConfig(undefined, DEFAULT_TENANT);
    setActiveTenant(DEFAULT_TENANT);
    queryClient.clear();
    await new Promise(resolve => setTimeout(resolve, 50));
    setIsAuthTransitioning(false);
  }, [removeToken, queryClient]);

  const switchTenant = useCallback(async (newTenantId: string) => {
    if (newTenantId === currentTenant) return;

    setIsSwitchingTenant(true);

    setActiveTenant(newTenantId);
    setStoredTenant(newTenantId);

    applyHeadersConfig(token?.accessToken, newTenantId);

    await queryClient.invalidateQueries();

    setTimeout(() => {
      setIsSwitchingTenant(false);
    }, 800);

  }, [setStoredTenant, token, queryClient, currentTenant]);

  const login: AuthContext["login"] = useCallback(async (data) => {
    try {
      const baseUrl = getDynamicBaseUrl(env.VITE_APP_SMARTBPM_URL, '/api/smartbpm');

      const response = await fetch(`${baseUrl}/oauth/token`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Authorization": "Basic c21hcnRwbGF0Zm9ybTpzbUBydHBsQHRmQHJt",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "tenant": DEFAULT_TENANT
        },
        body: `client=smartplataform&username=${encodeURIComponent(data.login)}&password=${encodeURIComponent(data.password)}&grant_type=password`
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || errorData.message || i18n.t("auth.invalidCredentials"));
      }

      const rawData = await response.json();
      const resultData: PostOauthTokenResponse = {
        accessToken: rawData.access_token,
        refreshToken: rawData.refresh_token,
        expiresIn: rawData.expires_in,
        refreshExpiresIn: rawData.refresh_expires_in,
        tokenType: rawData.token_type,
      };

      applyHeadersConfig(resultData?.accessToken, currentTenant);
      localStorage.setItem("token", JSON.stringify(resultData));
      sessionStorage.removeItem("token");
      setTokenState(resultData);
      await fetchUserInfo();
    } catch (error) {
      throw error;
    }
  }, [fetchUserInfo, currentTenant]);

  const loginWithKeycloak: AuthContext["loginWithKeycloak"] = useCallback(async (keycloakToken) => {
    try {
      const response = await loginWithKeycloakToken({
        token: keycloakToken,
        tenant: currentTenant
      });

      const rawData = response as any;
      const resultData: PostOauthTokenResponse = {
        accessToken: rawData.access_token,
        refreshToken: rawData.refresh_token,
        expiresIn: rawData.expires_in,
        refreshExpiresIn: rawData.refresh_expires_in,
        tokenType: rawData.token_type,
      };

      applyHeadersConfig(resultData?.accessToken, currentTenant);
      localStorage.setItem("token", JSON.stringify(resultData));
      sessionStorage.removeItem("token");
      setTokenState(resultData);
      await fetchUserInfo();
    } catch (error) {
      throw error;
    }
  }, [fetchUserInfo, currentTenant]);

  useEffect(() => {
    if (!token?.accessToken) {
      setIsInitializing(false);
      return;
    }
    applyHeadersConfig(token?.accessToken, currentTenant);
    void fetchUserInfo();
  }, [token, fetchUserInfo]);

  const refreshAuthToken = async () => {
    try {
      setIsRefreshingToken(true);
      const currentToken = getStoredToken();

      if (!currentToken.refreshToken) {
        throw new Error("No refresh token available");
      }

      const baseUrl = getDynamicBaseUrl(env.VITE_APP_SMARTBPM_URL, '/api/smartbpm');

      const response = await fetch(`${baseUrl}/oauth/token`, {
        method: "POST",
        headers: {
          "Authorization": "Basic c21hcnRwbGF0Zm9ybTpzbUBydHBsQHRmQHJt",
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          "tenant": currentTenant
        },
        body: `client=smartplataform&grant_type=refresh_token&refresh_token=${currentToken.refreshToken}`
      });

      if (!response.ok) {
        throw new Error("Refresh token failed");
      }

      const rawData = await response.json();
      const newData: PostOauthTokenResponse = {
        accessToken: rawData.access_token,
        refreshToken: rawData.refresh_token,
        expiresIn: rawData.expires_in,
        refreshExpiresIn: rawData.refresh_expires_in,
        tokenType: rawData.token_type,
      };

      setToken(newData);
      applyHeadersConfig(newData.accessToken, currentTenant);
      setIsRefreshingToken(false);
      return newData.accessToken;
    } catch (error) {
      setIsRefreshingToken(false);
      throw error;
    }
  };

  useEffect(() => {
    let isRefreshing = false;

    const onFulfilled = (response: any) => response;

    const onRejected = async (error: any) => {
      if (error.config?.url?.includes("/oauth/token")) {
        return Promise.reject(error);
      }

      if (error.response?.status === 401) {
        if (isRefreshing) {
          return Promise.reject(error);
        }

        isRefreshing = true;

        try {
          const newAccessToken = await refreshAuthToken();

          if (newAccessToken) {
            isRefreshing = false;
            if (error.config) {
              error.config.headers.Authorization = `Bearer ${newAccessToken}`;
              return axiosInstance.request(error.config);
            }
          }
        } catch (e) {
          isRefreshing = false;
          await logout();
        }
      }
      return Promise.reject(error);
    };

    const responseInterceptors = [
      axiosInstance.interceptors.response
    ];

    const interceptorIds = responseInterceptors.map(i => i.use(onFulfilled, onRejected));

    return () => {
      responseInterceptors.forEach((i, idx) => i.eject(interceptorIds[idx]));
    };
  }, [logout, setToken, currentTenant]);

  useEffect(() => {
    if (user && isAuthTransitioning) {
      setIsAuthTransitioning(false);
    }
  }, [user, isAuthTransitioning]);

  const isLoading = !isRefreshingToken && (isInitializing || loadingUserInfo || isAuthTransitioning || (!!user && !user.isAdmin && loadingPermissions) || (!!user && loadingTenants) || (!!user && loadingOrganizations));

  return (
    <AuthContext.Provider value={{ isAuthenticated, user: user ? { ...user, tenant: currentTenant } : undefined, userTenant: user?.tenant, login, loginWithKeycloak, logout, isLoading, permissions, hasPermission, tenants, organizations, currentTenant, switchTenant, isSwitchingTenant }}>
      {isLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box> : children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
